import { useEffect, useRef } from 'react';
import { useAnalyticsConsent } from '@/contexts/CookieConsentContext';
import { GoogleAnalytics } from '@/lib/analytics';
import { IS_DEV } from '@/config/analytics';

type Metric = {
  name: string;
  value: number;
  delta?: number;
  id?: string;
};

export function useWebVitals() {
  const hasReported = useRef(false);
  const hasAnalyticsConsent = useAnalyticsConsent();

  useEffect(() => {
    // Only track web vitals if user has granted analytics consent
    if (!hasAnalyticsConsent) {
      if (IS_DEV) {
        console.log('[Web Vitals] Not tracking: analytics consent denied');
      }
      return;
    }

    if (hasReported.current || typeof window === 'undefined') {
      return;
    }

    hasReported.current = true;

    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const value = (entry as any).startTime;
          if (value) {
            reportWebVital({
              name: 'LCP',
              value,
              id: (entry as any).id
            });
          }
        });
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });

      return () => observer.disconnect();
    }
  }, [hasAnalyticsConsent]);
}

function reportWebVital(metric: Metric) {
  // Only send to analytics if Google Analytics is available
  if (GoogleAnalytics.isAvailable()) {
    // Convert CLS value to integer (multiply by 1000) as per previous logic
    const metricValue = metric.name === 'CLS' ? metric.value * 1000 : metric.value;
    
    GoogleAnalytics.trackEvent(
      'web_vitals',
      metric.name.toLowerCase(),
      metric.id,
      Math.round(metricValue)
    );
  }

  // Log to console in development
  if (IS_DEV) {
    console.log(`[Web Vitals] ${metric.name}:`, metric.value);
  }
}
